import styled from 'styled-components';
import type { ColorProps, LayoutProps, SpaceProps, TypographyProps } from 'styled-system';
import { color, layout, space, typography, variant } from 'styled-system';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { TextStyles } from '@ingka/typography-shared';
import Text from '@app/ui/Text/Text';

// Mapping from Kompis ThemeFontStyleTypeEnum to Skapa textStyle
const textStyleMap: Record<string, TextStyles> = {
  [ThemeFontStyleTypeEnum.bodySmall]: 'Body.S',
  [ThemeFontStyleTypeEnum.bodyMedium]: 'Body.M',
  [ThemeFontStyleTypeEnum.bodyLarge]: 'Body.L',
  [ThemeFontStyleTypeEnum.bodyXLarge]: 'Statement.M',
  [ThemeFontStyleTypeEnum.priceLarge]: 'Body.L'
};

// @see https://fundament.ikea.com/foundation/typography#typeStyles
type Type =
  | ThemeFontStyleTypeEnum.bodySmall
  | ThemeFontStyleTypeEnum.bodyMedium
  | ThemeFontStyleTypeEnum.bodyLarge
  | ThemeFontStyleTypeEnum.bodyXLarge
  | ThemeFontStyleTypeEnum.priceLarge;

type AsProps = {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  forwardedAs?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
};

type ExtendProps = React.PropsWithoutRef<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
> &
  SpaceProps &
  ColorProps &
  TypographyProps &
  LayoutProps &
  AsProps;

type BoxProps = ExtendProps & {
  variant?: Type;
};

const Box = styled.p<BoxProps>`
  margin: 0;
  word-break: normal;
  overflow-wrap: anywhere;
  ${color}
  ${space}
  ${layout}
  ${typography}
  ${variant({
    variants: {
      body12: {
        lineHeight: 'short'
      },
      body14: {
        lineHeight: 'shorter'
      },
      body16: {
        lineHeight: 'base'
      },
      body18: {
        lineHeight: 'base'
      }
    }
  })}
`;

export interface BodyProps extends ExtendProps {
  /** Type of body text as described in Fundament */
  type: Type;
  /** React element, text, or fragment */
  children: React.ReactNode;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

const Body = ({ type, bold, italic, children, color, ...props }: BodyProps) => {
  const textStyle = textStyleMap[type] || 'Body.M';
  // Bold/italic: applied to Box via styled-system, inherited by Text via CSS inherit
  const fontWeight = bold ? 'bold' : props.fontWeight;
  const fontStyle = italic ? 'italic' : undefined;

  return (
    <Box
      {...props}
      color={color ?? 'text'}
      variant={type}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
    >
      <Text tagName="span" textStyle={textStyle}>
        {children}
      </Text>
    </Box>
  );
};

export default Body;
